import React, { useCallback, useEffect, useState } from 'react';
import axios from '../api/axiosConfig';

// Apartado de "Cambios Trámites": CUPL ya PAGADOS que quedaron libres por un cambio de tipo
// (sin prenda <-> con prenda) y que se pueden asignar a otro trámite vigente sin volver a pagarlos.
const money = (v) => (v ? `$${Number(v).toLocaleString('es-CO')}` : '—');
const fecha = (d) => (d ? new Date(d).toLocaleDateString('es-CO', { timeZone: 'America/Bogota' }) : '—');
const vencido = (d) => d && new Date(d) < new Date(new Date().toDateString());

const btn = (bg, color = 'white') => ({ padding: '8px 14px', background: bg, color, border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', fontSize: '13px' });

const CuplLiberadosPanel = ({ onAsignado }) => {
  const [data, setData] = useState({ disponibles: [], reasignados: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [verHistorial, setVerHistorial] = useState(false);
  const [modal, setModal] = useState(null); // { cupl, placa, validacion, validando, guardando, error }

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/tramites/cupl-liberados');
      setData({ disponibles: res.data?.disponibles || [], reasignados: res.data?.reasignados || [] });
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cargar los CUPL pagados liberados.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const validar = async () => {
    const placa = modal.placa.trim().toUpperCase();
    if (!placa) return;
    setModal(m => ({ ...m, validando: true, error: '', validacion: null }));
    try {
      const res = await axios.get('/api/tramites/cupl-liberados/validar', { params: { cupl_number: modal.cupl.cupl_number, placa } });
      setModal(m => ({ ...m, validando: false, validacion: res.data }));
    } catch (err) {
      setModal(m => ({ ...m, validando: false, error: err.response?.data?.message || 'No se pudo validar la placa' }));
    }
  };

  const asignar = async () => {
    const v = modal.validacion;
    if (!v) return;
    setModal(m => ({ ...m, guardando: true, error: '' }));
    try {
      const res = await axios.post('/api/tramites/cupl-liberados/asignar', {
        cupl_number: modal.cupl.cupl_number,
        placa: v.placa,
        tramite_id: v.tramite_id,
        confirmar: v.advertencias?.length ? 'true' : 'false'
      });
      const notas = res.data.notas?.length ? `\n\nNotas:\n• ${res.data.notas.join('\n• ')}` : '';
      alert(`✅ ${res.data.message}.\nNo se descontó saldo porque el CUPL ya estaba pagado.${notas}`);
      setModal(null);
      cargar();
      if (onAsignado) onAsignado();
    } catch (err) {
      setModal(m => ({ ...m, guardando: false, error: err.response?.data?.message || 'No se pudo asignar el CUPL' }));
    }
  };

  const { disponibles, reasignados } = data;
  const totalValor = disponibles.reduce((s, x) => s + Number(x.total_amount || 0), 0);

  return (
    <div style={{ background: 'var(--card-bg)', border: '1px solid #fcd34d', borderRadius: '14px', padding: '18px 22px', marginBottom: '20px', boxShadow: 'var(--card-shadow)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '16px', color: 'var(--text-primary)' }}>💳 CUPL pagados por reutilizar</h3>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
            CUPL que ya se pagaron y quedaron libres por un cambio de tipo de trámite. Se asignan a otro trámite sin volver a pagar.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={{ background: disponibles.length ? '#fef3c7' : '#f1f5f9', color: disponibles.length ? '#92400e' : '#475569', padding: '8px 14px', borderRadius: '10px', fontWeight: 800, fontSize: '14px' }}>
            {loading ? '…' : `${disponibles.length} por usar · ${money(totalValor)}`}
          </div>
          <button type="button" onClick={() => setVerHistorial(v => !v)} style={btn('#e2e8f0', '#334155')} aria-expanded={verHistorial}>
            {verHistorial ? 'Ocultar asignados' : `Ver asignados (${reasignados.length})`}
          </button>
        </div>
      </div>

      {error && <div className="error-banner" style={{ marginTop: '12px' }}>{error}</div>}

      {!loading && disponibles.length > 0 && (
        <div className="table-responsive" style={{ marginTop: '14px' }}>
          <table className="users-table">
            <thead>
              <tr>
                <th>CUPL</th><th>Valor</th><th>Tipo del CUPL</th><th>Trámite origen</th><th>Cambio</th><th>Liberado</th><th>Fecha límite</th><th>Acción</th>
              </tr>
            </thead>
            <tbody>
              {disponibles.map(x => (
                <tr key={x.cupl_number}>
                  <td data-label="CUPL"><strong>{x.cupl_number}</strong></td>
                  <td data-label="Valor">{money(x.total_amount)}</td>
                  <td data-label="Tipo del CUPL">{x.cupl_tipo_name || '—'}</td>
                  <td data-label="Origen">#{x.tramite_origen_id || '—'} {x.placa_origen && <span className="plate-badge-sm">{x.placa_origen}</span>}<br /><span style={{ fontSize: '11px', color: '#64748b' }}>{x.transito_origen || ''}</span></td>
                  <td data-label="Cambio" style={{ fontSize: '12px' }}>{x.tipo_anterior ? <>{x.tipo_anterior} → <strong>{x.tipo_nuevo}</strong></> : '—'}</td>
                  <td data-label="Liberado" style={{ fontSize: '12px' }}>{fecha(x.liberado_at)}<br /><span style={{ color: '#64748b' }}>{x.liberado_por || ''}</span></td>
                  <td data-label="Fecha límite" style={{ color: vencido(x.expiration_date) ? '#b91c1c' : undefined, fontWeight: vencido(x.expiration_date) ? 700 : 400 }}>
                    {fecha(x.expiration_date)}{vencido(x.expiration_date) ? ' (pasada)' : ''}
                  </td>
                  <td data-label="Acción">
                    <button type="button" style={btn('#d97706')} onClick={() => setModal({ cupl: x, placa: '', validacion: null, validando: false, guardando: false, error: '' })}>
                      ➡️ Asignar a trámite
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading && disponibles.length === 0 && !error && (
        <p style={{ margin: '12px 0 0', fontSize: '13px', color: '#64748b' }}>No hay CUPL pagados pendientes por reutilizar.</p>
      )}

      {verHistorial && (
        <div className="table-responsive" style={{ marginTop: '14px' }}>
          {reasignados.length === 0 ? (
            <p style={{ fontSize: '13px', color: '#64748b' }}>Aún no se ha reasignado ningún CUPL.</p>
          ) : (
            <table className="users-table">
              <thead><tr><th>CUPL</th><th>Valor</th><th>Origen</th><th>Asignado a</th><th>Fecha</th><th>Por</th></tr></thead>
              <tbody>
                {reasignados.map(x => (
                  <tr key={x.cupl_number}>
                    <td data-label="CUPL">{x.cupl_number}</td>
                    <td data-label="Valor">{money(x.total_amount)}</td>
                    <td data-label="Origen">#{x.tramite_origen_id || '—'}</td>
                    <td data-label="Asignado a"><strong>#{x.reasignado_tramite_id}</strong> {x.placa_destino && <span className="plate-badge-sm">{x.placa_destino}</span>}</td>
                    <td data-label="Fecha">{fecha(x.reasignado_at)}</td>
                    <td data-label="Por">{x.reasignado_por || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="asignar-cupl-title">
          <div className="modal-content" style={{ maxWidth: '560px', width: '95%', padding: '24px' }}>
            <h3 id="asignar-cupl-title" style={{ marginTop: 0 }}>➡️ Asignar CUPL pagado</h3>
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', padding: '12px 14px', fontSize: '13px', marginBottom: '16px' }}>
              <div><strong>CUPL:</strong> {modal.cupl.cupl_number}</div>
              <div><strong>Valor:</strong> {money(modal.cupl.total_amount)} · <strong>Tipo:</strong> {modal.cupl.cupl_tipo_name || '—'}</div>
              <div><strong>Viene del trámite:</strong> #{modal.cupl.tramite_origen_id} {modal.cupl.placa_origen || ''}</div>
              <div style={{ color: '#15803d', fontWeight: 700, marginTop: '4px' }}>Ya está pagado: se asigna sin descontar saldo.</div>
            </div>

            <label htmlFor="asignar-placa" style={{ fontSize: '11px', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Placa del trámite destino</label>
            <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
              <input
                id="asignar-placa"
                type="text"
                autoFocus
                value={modal.placa}
                placeholder="Ej. ABC12D"
                onChange={e => setModal(m => ({ ...m, placa: e.target.value.toUpperCase(), validacion: null, error: '' }))}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); validar(); } }}
                style={{ flex: 1, padding: '10px 14px', border: '1px solid var(--input-border)', borderRadius: '10px', fontWeight: 700, letterSpacing: '1px' }}
              />
              <button type="button" onClick={validar} disabled={modal.validando || !modal.placa.trim()} style={btn('#2563eb')}>
                {modal.validando ? '⏳' : '🔍 Validar'}
              </button>
            </div>

            {modal.error && <div role="alert" style={{ marginTop: '12px', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 600 }}>❌ {modal.error}</div>}

            {modal.validacion && (
              <div style={{ marginTop: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '12px 14px', fontSize: '13px' }}>
                <div style={{ fontWeight: 800, color: '#15803d', marginBottom: '4px' }}>✅ Trámite encontrado</div>
                <div><strong>Trámite:</strong> #{modal.validacion.tramite_id} · <span className="plate-badge-sm">{modal.validacion.placa}</span></div>
                <div><strong>Propietario:</strong> {modal.validacion.propietario || '—'} {modal.validacion.documento ? `(${modal.validacion.documento})` : ''}</div>
                <div><strong>Tipo:</strong> {modal.validacion.tipo} · <strong>Tránsito:</strong> {modal.validacion.transito}</div>
                <div><strong>Estado:</strong> {modal.validacion.estado}</div>
                {modal.validacion.advertencias?.length > 0 && (
                  <div style={{ marginTop: '10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '8px 10px', color: '#92400e' }}>
                    <strong>⚠️ Tenga en cuenta:</strong>
                    <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
                      {modal.validacion.advertencias.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="modal-actions" style={{ marginTop: '18px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button type="button" className="btn-secondary" onClick={() => setModal(null)} disabled={modal.guardando}>Cancelar</button>
              <button type="button" onClick={asignar} disabled={!modal.validacion || modal.guardando} style={{ ...btn(modal.validacion ? '#16a34a' : '#94a3b8'), cursor: modal.validacion ? 'pointer' : 'not-allowed' }}>
                {modal.guardando ? '⏳ Asignando...' : modal.validacion?.advertencias?.length ? '✅ Confirmar y asignar' : '✅ Asignar CUPL'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CuplLiberadosPanel;
